import { useQuery } from "@apollo/client";
import { sortBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import {
  GetCareerRoleRelatedDataQueryQuery,
  GetCareerRoleRelatedDataQueryQueryVariables,
  GetUserRolesDetailsQueryQuery,
  GetUserRolesDetailsQueryQueryVariables,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import getUserRolesDetailsQuery from "@apps/assessments/graphql/get-user-roles-details-query";
import {
  mapCareerDevelopmentGoalsWithCompetencyIds,
  mapCompetencyAverageAssessmentAnswersById,
} from "@apps/profile/career-tab/components/career-table-helpers";
import Competency from "@apps/profile/career-tab/components/competency";
import Responsibilities from "@apps/profile/career-tab/components/responsibilities";
import getCareerRoleRelatedDataQuery from "@apps/profile/career-tab/graphql/get-career-role-related-data-query";
import Loading from "@components/loading/loading";
import { Select } from "@components/select/select";
import { assertEdgesNonNull } from "@helpers/helpers";

const EmployeeDrawerRoles = ({ target }: { target: BasicUser }) => {
  const [selectedRoleId, setSelectedRoleId] = useState<number>(0);

  const { data, loading } = useQuery<
    GetUserRolesDetailsQueryQuery,
    GetUserRolesDetailsQueryQueryVariables
  >(getUserRolesDetailsQuery, {
    variables: { userId: target.id },
  });

  const currentRoles = useMemo(() => {
    const unsortedRoles = data?.user?.currentRoles || [];
    return sortBy(
      unsortedRoles,
      (role) => `${role.careerTrack.id}-${role.level}`,
    );
  }, [data]);
  const futureRoles = useMemo(() => {
    const unsortedRoles = data?.user?.futureRoles || [];
    return sortBy(
      unsortedRoles,
      (role) => `${role.careerTrack.id}-${role.level}`,
    );
  }, [data]);

  const options = useMemo(
    () => [
      ...currentRoles.map((role) => ({
        label: `Current Role: ${role.title} (Level ${role.level})`,
        value: role.id,
      })),
      ...futureRoles.map((role) => ({
        label: `Future Role: ${role.title} (Level ${role.level})`,
        value: role.id,
      })),
    ],
    [currentRoles, futureRoles],
  );

  const selectedRole = useMemo(
    () =>
      [...currentRoles, ...futureRoles].find(
        (role) => role.id === selectedRoleId,
      ),
    [currentRoles, futureRoles, selectedRoleId],
  );
  const competencies = selectedRole?.competencies
    ? assertEdgesNonNull(selectedRole.competencies)
    : [];
  const responsibilities = selectedRole?.responsibilities
    ? assertEdgesNonNull(selectedRole.responsibilities)
    : [];

  useEffect(() => {
    if (selectedRoleId === 0 && currentRoles.length > 0) {
      setSelectedRoleId(currentRoles[0].id);
    }
  }, [currentRoles, selectedRoleId]);

  const { data: roleData, loading: roleDataLoading } = useQuery<
    GetCareerRoleRelatedDataQueryQuery,
    GetCareerRoleRelatedDataQueryQueryVariables
  >(getCareerRoleRelatedDataQuery, {
    skip: !selectedRole,
    variables: {
      profileId: target.id,
      roleId: selectedRole?.id || 0,
    },
  });

  const developmentGoals = useMemo(() => {
    return mapCareerDevelopmentGoalsWithCompetencyIds(roleData);
  }, [roleData]);

  const currentRoleAverageAssessmentAnswersByCompetencyId = useMemo(() => {
    return mapCompetencyAverageAssessmentAnswersById(roleData);
  }, [roleData]);

  const profile = data?.user;

  return (
    <div className="p-6">
      {loading ? (
        <Loading>Loading roles...</Loading>
      ) : !selectedRole || currentRoles.length === 0 ? (
        <div className="text-gray-500">No roles found</div>
      ) : (
        <Select<number>
          value={selectedRoleId}
          options={options}
          onChange={(option) => setSelectedRoleId(option.value)}
        />
      )}
      <div className="mt-6 empty:hidden">
        {roleDataLoading ? (
          <Loading className="py-6">Loading role...</Loading>
        ) : profile &&
          (competencies.length > 0 || responsibilities.length > 0) ? (
          <div className="flex flex-col gap-6">
            {competencies.map((competency) => (
              <div key={competency.id} className="border rounded-md p-6">
                <Competency
                  profile={profile}
                  competency={competency}
                  role={selectedRole}
                  goals={developmentGoals.filter(({ competencyIds }) =>
                    competencyIds.includes(competency.id),
                  )}
                  criteria={assertEdgesNonNull(competency.criteria)}
                  averageAssessmentAnswers={
                    currentRoleAverageAssessmentAnswersByCompetencyId[
                      competency.id
                    ]
                  }
                />
              </div>
            ))}
            {responsibilities.length > 0 && (
              <div key={"responsibilities"} className="border rounded-md p-6">
                <div className="font-medium text-gray-700 mb-2">
                  Responsibilities
                </div>
                <Responsibilities
                  careerRole={roleData?.careerRole}
                  profile={profile}
                  responsibilities={responsibilities}
                />
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default EmployeeDrawerRoles;
